import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const emptyDeal = { vehicle_id: '', customer_id: '', sale_price: '', down_payment_received: '', weekly_payment: '', monthly_payment: '', payment_frequency: 'weekly', first_payment_date: '', term_weeks: '', notes: '' };

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [form, setForm] = useState(emptyDeal);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingGHL, setSearchingGHL] = useState(false);

  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    setSearchingGHL(true);
    // Search local customers
    const local = customers.filter(c =>
      `${c.first_name} ${c.last_name} ${c.phone} ${c.email}`.toLowerCase().includes(q.toLowerCase())
    ).map(c => ({ ...c, source: 'local' }));
    // Search GHL
    try {
      const r = await fetch(`/api/customers/ghl/search?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('rz_token') }
      });
      const d = await r.json();
      const ghlContacts = (d.contacts || []).map(c => ({
        id: null,
        ghl_contact_id: c.id,
        first_name: c.firstName,
        last_name: c.lastName,
        email: c.email,
        phone: c.phone,
        address: c.address1,
        city: c.city,
        state: c.state,
        zip: c.postalCode,
        source: 'ghl'
      })).filter(g => !local.find(l => l.phone === g.phone || l.email === g.email));
      setCustomerResults([...local, ...ghlContacts]);
    } catch(e) {
      setCustomerResults(local);
    }
    setSearchingGHL(false);
  };

  const selectCustomer = async (c) => {
    if (c.source === 'ghl' && !c.id) {
      // Create in RZ DMS from GHL contact
      const r = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('rz_token'), 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: c.first_name, last_name: c.last_name, email: c.email, phone: c.phone, address: c.address, city: c.city, state: c.state, zip: c.zip, ghl_contact_id: c.ghl_contact_id })
      });
      const created = await r.json();
      setSelectedCustomer(created);
      set('customer_id', created.id);
    } else {
      setSelectedCustomer(c);
      set('customer_id', c.id);
    }
    setCustomerSearch('');
    setCustomerResults([]);
  };

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    Promise.all([api.getDeals(), api.getVehicles(), api.getCustomers()]).then(([d, v, c]) => {
      setDeals(Array.isArray(d) ? d : []);
      setVehicles(Array.isArray(v) ? v.filter(v => v.status === 'available') : []);
      setCustomers(Array.isArray(c) ? c : []);
      setLoading(false);
    });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const d = await api.saveDeal(form);
    setSaving(false);
    if (d.error) { alert(d.error); return; }
    setModal(false);
    load();
  };

  const viewDeal = async (id) => {
    const d = await api.getDeal(id);
    setSelectedDeal(d);
    setViewModal(true);
  };

  const deleteDeal = async (id, name) => {
    if (!window.confirm('Delete deal for ' + name + '? This will also delete all payment records and mark the vehicle as available again. This cannot be undone.')) return;
    await fetch('/api/deals/' + id, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + localStorage.getItem('rz_token') } });
    load();
  };

  const unmarkPayment = async (dealId, paymentId) => {
    if (!window.confirm('Mark this payment as unpaid?')) return;
    await fetch('/api/deals/' + dealId + '/unpayment/' + paymentId, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('rz_token') }
    });
    viewDeal(dealId);
    load();
  };

  const recordPayment = async (dealId, paymentId, amount) => {
    const methods = ['cash', 'check', 'card', 'money order'];
    const choice = window.prompt('Payment method:\n1. Cash\n2. Check\n3. Card\n4. Money Order\nEnter number:', '1');
    const method = methods[(parseInt(choice) || 1) - 1] || 'cash';
    const result = await api.recordPayment(dealId, { payment_id: paymentId, amount, paid_date: new Date().toISOString().split('T')[0], payment_method: method });
    if (result.error) { alert(result.error); return; }
    viewDeal(dealId);
    load();
  };

  const active = deals.filter(d => d.status === 'active').length;
  const overdue = deals.reduce((s, d) => s + parseInt(d.payments_overdue || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h1>Deal <span>Management</span></h1>
        <button className="btn-red" onClick={() => { setForm(emptyDeal); setModal(true); }}>+ New Deal</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        <div className="stat-card red"><div className="label">Active Deals</div><div className="value">{active}</div></div>
        <div className="stat-card"><div className="label">Overdue Payments</div><div className="value" style={{ color: overdue > 0 ? '#ff4444' : '#e0e0e0' }}>{overdue}</div></div>
        <div className="stat-card"><div className="label">Total Deals</div><div className="value">{deals.length}</div></div>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <table className="data-table">
          <thead><tr><th>Vehicle</th><th>Customer</th><th>Sale Price</th><th>Payment</th><th>Progress</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {deals.map(d => {
              const overdueCnt = parseInt(d.payments_overdue || 0);
              const made = parseInt(d.payments_made || 0);
              const total = parseInt(d.total_payments || 0);
              const pct = total ? Math.round((made / total) * 100) : 0;
              return (
                <tr key={d.id}>
                  <td><strong>{d.year} {d.make} {d.model}</strong><br /><span style={{ fontSize: 11, color: '#555', fontFamily: 'monospace' }}>{d.vin}</span></td>
                  <td><strong>{d.first_name} {d.last_name}</strong><br /><span style={{ fontSize: 11, color: '#555' }}>{d.phone}</span></td>
                  <td style={{ color: '#cc0000', fontWeight: 700 }}>${Number(d.sale_price || 0).toLocaleString()}<br /><span style={{ fontSize: 11, color: '#555' }}>${Number(d.down_payment_received || 0).toLocaleString()} down</span></td>
                  <td>${Number(d.weekly_payment || d.monthly_payment || 0).toLocaleString()}<br /><span style={{ fontSize: 11, color: '#555' }}>{d.payment_frequency}</span></td>
                  <td>
                    <div style={{ background: '#222', borderRadius: 4, height: 6, width: 100 }}>
                      <div style={{ background: '#cc0000', height: 6, borderRadius: 4, width: `${pct}%` }} />
                    </div>
                    <span style={{ fontSize: 11, color: '#555' }}>{made}/{total} paid</span>
                  </td>
                  <td><span style={{ color: overdueCnt > 0 ? '#ff4444' : '#4caf50', fontWeight: 700, fontSize: 12 }}>{overdueCnt > 0 ? `${overdueCnt} OVERDUE` : d.status.toUpperCase()}</span></td>
                  <td style={{display:"flex",gap:4}}><button className="btn-ghost" style={{borderColor:"#0066cc",color:"#0066cc"}} onClick={() => viewDeal(d.id)}>View</button><button className="btn-ghost" style={{borderColor:"#4caf50",color:"#4caf50"}} onClick={() => window.open("/api/pdf/buyers-order/"+d.id+"?token="+localStorage.getItem("rz_token"), "_blank")}>PDF</button><button className="btn-ghost" style={{borderColor:"#ff4444",color:"#ff4444"}} onClick={() => deleteDeal(d.id, d.first_name+" "+d.last_name)}>Delete</button></td>
                </tr>
              );
            })}
            {!deals.length && <tr><td colSpan={7}><div className="empty-state"><div className="icon">🤝</div><p>No deals yet.</p></div></td></tr>}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header"><h2>New Deal</h2><button className="modal-close" onClick={() => setModal(false)}>×</button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Vehicle</label>
                  <select value={form.vehicle_id} onChange={e => set('vehicle_id', e.target.value)}>
                    <option value="">Select available vehicle...</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} {v.trim} — {v.vin}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ position: 'relative' }}><label>Customer</label>
                  {selectedCustomer ? (
                    <div style={{ background: '#111', border: '1px solid #cc0000', borderRadius: 6, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#e0e0e0' }}>{selectedCustomer.first_name} {selectedCustomer.last_name} — {selectedCustomer.phone}</span>
                      <button onClick={() => { setSelectedCustomer(null); set('customer_id', ''); }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 16 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input value={customerSearch} onChange={e => searchCustomers(e.target.value)} placeholder="Search by name, phone, or email..." />
                      {customerResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, zIndex: 100, maxHeight: 200, overflowY: 'auto' }}>
                          {customerResults.map((c, i) => (
                            <div key={i} onClick={() => selectCustomer(c)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between' }}
                              onMouseEnter={e => e.currentTarget.style.background='#222'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                              <span style={{ color: '#e0e0e0' }}>{c.first_name} {c.last_name} — {c.phone}</span>
                              <span style={{ fontSize: 11, color: c.source === 'ghl' ? '#cc0000' : '#555' }}>{c.source === 'ghl' ? 'GHL' : 'RZ DMS'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchingGHL && <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Searching...</div>}
                    </>
                  )}
                </div>
                <div className="form-group"><label>Sale Price</label><input type="number" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} placeholder="12995" /></div>
                <div className="form-group"><label>Down Payment Received</label><input type="number" value={form.down_payment_received} onChange={e => set('down_payment_received', e.target.value)} placeholder="1500" /></div>
                <div className="form-group"><label>Payment Frequency</label>
                  <select value={form.payment_frequency} onChange={e => set('payment_frequency', e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly (Every 2 Weeks)</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="form-group"><label>{form.payment_frequency === 'weekly' ? 'Weekly' : form.payment_frequency === 'biweekly' ? 'Bi-Weekly' : 'Monthly'} Payment</label>
                  <input type="number" value={form.payment_frequency === 'weekly' ? form.weekly_payment : form.monthly_payment}
                    onChange={e => set(form.payment_frequency === 'weekly' ? 'weekly_payment' : 'monthly_payment', e.target.value)} placeholder="125" />
                </div>
                <div className="form-group"><label>First Payment Date</label><input type="date" value={form.first_payment_date} onChange={e => set('first_payment_date', e.target.value)} /></div>
                <div className="form-group"><label>Term (weeks)</label><input type="number" value={form.term_weeks} onChange={e => set('term_weeks', e.target.value)} placeholder="104" /></div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-red" onClick={save} disabled={saving}>{saving ? 'Creating...' : 'Create Deal'}</button>
              <button className="btn-outline" onClick={() => setModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {viewModal && selectedDeal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(false)}>
          <div className="modal" style={{ maxWidth: 800 }}>
            <div className="modal-header">
              <h2>{selectedDeal.year} {selectedDeal.make} {selectedDeal.model} — {selectedDeal.first_name} {selectedDeal.last_name}</h2>
              <button className="modal-close" onClick={() => setViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div className="stat-card"><div className="label">Sale Price</div><div className="value" style={{ fontSize: 20 }}>${Number(selectedDeal.sale_price || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Down Payment</div><div className="value" style={{ fontSize: 20 }}>${Number(selectedDeal.down_payment_received || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Amount Financed</div><div className="value" style={{ fontSize: 20 }}>${Number(selectedDeal.total_amount_financed || 0).toLocaleString()}</div></div>
              </div>
              <h3 style={{ color: '#cc0000', marginBottom: 12, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>Payment Schedule</h3>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table className="data-table">
                  <thead><tr><th>#</th><th>Due Date</th><th>Amount</th><th>Status</th><th>Paid Date</th><th>Action</th></tr></thead>
                  <tbody>
                    {(selectedDeal.payments || []).map((p, i) => {
                      const overdue = p.status === 'pending' && new Date(p.due_date) < new Date();
                      return (
                        <tr key={p.id}>
                          <td style={{ color: '#555' }}>{i + 1}</td>
                          <td>{p.due_date ? new Date(p.due_date).toLocaleDateString('en-US') : '—'}</td>
                          <td>${Number(p.amount).toLocaleString()}</td>
                          <td><span style={{ color: p.status === 'paid' ? '#4caf50' : overdue ? '#ff4444' : '#ff9800', fontWeight: 700, fontSize: 11 }}>{p.status === 'paid' ? 'PAID' : overdue ? 'OVERDUE' : 'PENDING'}</span></td>
                          <td style={{ color: '#555', fontSize: 12 }}>{p.paid_date ? new Date(p.paid_date).toLocaleDateString('en-US') : '—'}{p.payment_method ? <span style={{marginLeft:6,color:'#888',fontSize:11}}>({p.payment_method})</span> : null}</td>
                          <td style={{display:'flex',gap:4}}>{p.status !== 'paid' ? <button className="btn-ghost" style={{ borderColor: '#4caf50', color: '#4caf50' }} onClick={() => recordPayment(selectedDeal.id, p.id, p.amount)}>Mark Paid</button> : <button className="btn-ghost" style={{ borderColor: '#ff9800', color: '#ff9800', fontSize:11 }} onClick={() => unmarkPayment(selectedDeal.id, p.id)}>Undo</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
